import type { FilterOption } from '@bridge/types';

/**
 * Backing fiat currency for stablecoins and display formatting
 */
export const BACKING_CURRENCY = {
  Brl: 'brl',
  Cop: 'cop',
  Eur: 'eur',
  Gbp: 'gbp',
  Mxn: 'mxn',
  Usd: 'usd',
} as const;

export type BackingCurrency = (typeof BACKING_CURRENCY)[keyof typeof BACKING_CURRENCY];

/**
 * Currency configuration — single source of truth for a currency.
 *
 * `sessionFlag` is an opaque tag: the registry never interprets it directly,
 * it just passes the config to the caller-provided `canAccess` callback.
 *
 * @template F - Union of valid session-flag strings. Pin to a typed union
 *   (e.g. a consuming app's `SessionFlag`) to get compile-time enforcement
 *   that every `sessionFlag` in the config is a member of that union.
 *   Defaults to `string` when flag validation is not needed.
 */
export interface CurrencyConfig<F extends string = string> {
  /** Display label (e.g., 'USDC', 'USDT') */
  label: string;
  /** Backing fiat currency for display formatting */
  backingCurrency: BackingCurrency;
  /** Whether this is a stablecoin (vs pure crypto like BTC/ETH) */
  isStablecoin: boolean;
  /** Logo file path (optional) */
  logoPath?: string;
  /** Session-gating tag — read only by the canAccess callback, not by the registry itself */
  sessionFlag?: F | null;
  /** Deprecated currencies are excluded from creation form dropdowns but still renderable for historic records */
  isDeprecated?: boolean;
  /** Limited Developer sessions see only currencies with this set; the sessionFlag gate still applies on top */
  enabledForLimitedDeveloper?: boolean;
}

/**
 * Currency registry interface — methods for accessing currency data.
 *
 * @template T - Currency ID union (e.g., 'usdc' | 'eurc' | 'usd')
 * @template S - Session type used for access gating (e.g., DecoratedSession, AdminSession)
 */
export interface CurrencyRegistry<T extends string, S = unknown> {
  // Session-agnostic
  getLabel(currency: string): string;
  getBackingCurrency(currency: T): BackingCurrency;
  isStablecoin(currency: T): boolean;
  getLogoPath(currency: T): string | undefined;
  getAllCurrencies(): T[];
  getConfig(currency: T): CurrencyConfig | undefined;

  // Session-aware — S is the caller's session type
  canAccess(currency: T, session: S | null): boolean;
  getAccessibleCurrencies(session: S | null): T[];
  getDropdownItems(session: S | null): FilterOption<T>[];
  getDropdownItemsFor(currencies: T[], session: S | null): FilterOption<T>[];
}
